import React, { useState, useMemo } from 'react';
import sortBy from 'lodash/sortBy';
import debounce from 'lodash/debounce';
import { withStyles } from '@material-ui/core/styles';
import { DateTime } from 'luxon';

import MuiPaper from '@material-ui/core/Paper';
import MuiTextField from '@material-ui/core/TextField';
import MuiGrid from '@material-ui/core/Grid';
import MuiInputAdornment from '@material-ui/core/InputAdornment';
import MuiSearchIcon from '@material-ui/icons/Search';
import MuiFormControl from '@material-ui/core/FormControl';
import MuiSelect from '@material-ui/core/Select';
import MuiMenuItem from '@material-ui/core/MenuItem';
import MuiInputLabel from '@material-ui/core/InputLabel';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import MuiTypography from '@material-ui/core/Typography';

import ViewWrapper from 'views/ViewWrapper';
import TableDistributionHistory from 'components/TableDistributionHistory';
import { isUpc } from 'utils/helpers';
import useInterval from 'hooks/useInterval';
import { useQuery } from 'react-query';
import { getDsps } from '../../dataLayer/dsps';

import * as API from '../../api';
const DistributionJobs = API.DistributionJobs();

const styles = theme => ({
  paper: {
    overflow: 'hidden',
    padding: theme.spacing(2),
  },
  title: {
    paddingBottom: theme.spacing(2),
    textTransform: 'capitalize',
  },
  actionRow: {
    display: 'flex',
    paddingBottom: theme.spacing(1),
  },
  actionButton: {
    marginRight: theme.spacing(1),
  },
  formControl: {
    minWidth: 120,
    marginLeft: theme.spacing(1),
  },
  dateText: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(1),
    color: 'gray',
  },
});

const DistributionHistory = ({ ...props }) => {
  const { classes } = props;

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDsp, setSelectedDsp] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [lastFetchedAt, setLastFetchedAt] = useState(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS));

  const shouldRefreshData = page === 0;

  async function refreshData() {
    await refetch();
    setLastFetchedAt(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS));
  }

  const { data: dsps, isLoading: isLoadingDsps } = useQuery(['getDsps'], getDsps, {
    refetchOnWindowFocus: false,
    select: data => data?.data?.data?.results,
  });

  const {
    data: distributionJobs,
    isLoading: isLoadingDstributionJobs,
    refetch,
  } = useQuery(['distributionJobs', searchTerm, selectedDsp, page, pageSize], async () => {
    return DistributionJobs.history({
      search: !isUpc(searchTerm) ? searchTerm : undefined,
      upc: isUpc(searchTerm) ? searchTerm : undefined,
      selectedDsp,
      pageSize,
      page,
    });
  });

  // start polling for updates to the list every 5 minutes
  useInterval(() => {
    if (shouldRefreshData) {
      refreshData(searchTerm, selectedDsp);
    }
  }, 60 * 1000 * 5);

  const debouncedSearch = useMemo(
    () =>
      debounce(value => {
        setSearchTerm(value);
        setPage(0);
      }, 350),
    []
  );

  const handleSearch = event => {
    debouncedSearch(event.target.value);
  };

  const handleDspChange = e => {
    setSelectedDsp(e.target.value);
    setPage(0);
  };

  return (
    <ViewWrapper>
      <MuiGrid item xs={12}>
        <MuiPaper className={classes.paper}>
          <MuiTypography className={classes.title} variant="h5">
            Distribution History
          </MuiTypography>
          <div className={classes.actionRow}>
            <MuiTextField
              variant="outlined"
              label="Search by title or UPC"
              placeholder="Release title or UPC"
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <MuiInputAdornment position="start">
                    <MuiSearchIcon />
                  </MuiInputAdornment>
                ),
              }}
            />
            {isLoadingDsps ? (
              <MuiCircularProgress />
            ) : (
              <MuiFormControl variant="outlined" className={classes.formControl}>
                <MuiInputLabel>DSP</MuiInputLabel>
                <MuiSelect label="DSP" value={selectedDsp} onChange={handleDspChange}>
                  <MuiMenuItem key="empty" value="">
                    All DSPs
                  </MuiMenuItem>
                  {sortBy(dsps, ['title']).map(dsp => (
                    <MuiMenuItem key={dsp.identifier} value={dsp.identifier}>
                      {dsp.name}
                    </MuiMenuItem>
                  ))}
                </MuiSelect>
              </MuiFormControl>
            )}
            <MuiTypography variant="body2" className={classes.dateText}>
              Last refreshed at {lastFetchedAt} {shouldRefreshData ? '(auto refresh every 5 mins)' : ''}
            </MuiTypography>
          </div>
          <TableDistributionHistory
            isLoading={isLoadingDstributionJobs}
            searchTerm={searchTerm}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            distributionJobs={distributionJobs}
          />
        </MuiPaper>
      </MuiGrid>
    </ViewWrapper>
  );
};
export default withStyles(styles)(DistributionHistory);
